/**
 * Manpower P&L — Cost dashboards API service (READ-ONLY, all GET).
 *
 * Types come from the OpenAPI-generated client (src/api/generated/manpower-cost.ts).
 * Every response is the standard envelope { status, statusCode, message, data };
 * JWT + city scoping are handled by the shared axios client (auth interceptor in api.ts).
 * baseURL already ends in /v1/api, so relative paths are /manpower/cost/...
 * All money fields arrive as decimal STRINGS — coerce with Number() at display time.
 */
import { apiService } from './api';
import type { components } from '../api/generated/manpower-cost';

// Single source of truth for the cost shapes — straight from the contract.
export type CityDailyCost = components['schemas']['CityDailyCost'];
export type CityMonthlyCost = components['schemas']['CityMonthlyCost'];
export type FacilityDailyCostRow = components['schemas']['FacilityDailyCostRow'];
export type WeeklyCostBuckets = components['schemas']['WeeklyCostBuckets'];
export type ShiftCostRow = components['schemas']['ShiftCostRow'];

/** Standard envelope with a typed `data` payload. */
interface Envelope<T> {
	status: boolean;
	statusCode: number;
	message: string;
	data: T;
}

export class ManpowerCostApiService {
	/**
	 * GET /manpower/cost/city?cityId=&date= — stored daily cost aggregate for one city.
	 * `data` may come back as an object OR a single-element array depending on backend mood;
	 * callers should normalise. cityId is optional (non-leadership coerced to own city).
	 */
	static async getCityDaily(params: {
		cityId?: number;
		date: string;
	}): Promise<Envelope<CityDailyCost | CityDailyCost[]>> {
		const search = new URLSearchParams();
		if (params.cityId != null) search.set('cityId', String(params.cityId));
		search.set('date', params.date);
		const res = await apiService.get(`/manpower/cost/city?${search.toString()}`);
		return res as unknown as Envelope<CityDailyCost | CityDailyCost[]>;
	}

	/** GET /manpower/cost/city/monthly?cityId=&month= — monthly cost with week1-4 + est. */
	static async getCityMonthly(params: {
		cityId?: number;
		month: string;
	}): Promise<Envelope<CityMonthlyCost>> {
		const search = new URLSearchParams();
		if (params.cityId != null) search.set('cityId', String(params.cityId));
		search.set('month', params.month);
		const res = await apiService.get(`/manpower/cost/city/monthly?${search.toString()}`);
		return res as unknown as Envelope<CityMonthlyCost>;
	}

	/** GET /manpower/cost/daily?facilityId=&date= — per-vendor cost rows for a facility/day. */
	static async getFacilityDaily(params: {
		facilityId: number;
		date: string;
	}): Promise<Envelope<FacilityDailyCostRow[]>> {
		const search = new URLSearchParams();
		search.set('facilityId', String(params.facilityId));
		search.set('date', params.date);
		const res = await apiService.get(`/manpower/cost/daily?${search.toString()}`);
		return res as unknown as Envelope<FacilityDailyCostRow[]>;
	}

	/** GET /manpower/cost/weekly?facilityId=&month= — facility week1-4 buckets (numeric). */
	static async getFacilityWeekly(params: {
		facilityId: number;
		month: string;
	}): Promise<Envelope<WeeklyCostBuckets>> {
		const search = new URLSearchParams();
		search.set('facilityId', String(params.facilityId));
		search.set('month', params.month);
		const res = await apiService.get(`/manpower/cost/weekly?${search.toString()}`);
		return res as unknown as Envelope<WeeklyCostBuckets>;
	}

	/** GET /manpower/cost/all-cities?date= — all-cities daily cost (leadership only). */
	static async getAllCitiesDaily(params: {
		date: string;
	}): Promise<Envelope<CityDailyCost[]>> {
		const search = new URLSearchParams();
		search.set('date', params.date);
		const res = await apiService.get(`/manpower/cost/all-cities?${search.toString()}`);
		return res as unknown as Envelope<CityDailyCost[]>;
	}

	/** GET /manpower/cost/shift/{shiftId} — per-vendor cost rows for a single shift. */
	static async getShiftCost(shiftId: number): Promise<Envelope<ShiftCostRow[]>> {
		const res = await apiService.get(`/manpower/cost/shift/${shiftId}`);
		return res as unknown as Envelope<ShiftCostRow[]>;
	}
}
